import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import { useUploadSocket } from "@/hooks/useUploadSocket";
import { showToast } from "@/components/ui/Toast";
import type { UploadUpdate } from "@/types/api";

interface UploadSocketContextValue {
  connected: boolean;
  clientId: string;
  addListener: (listener: (update: UploadUpdate) => void) => () => void;
}

const UploadSocketContext = createContext<UploadSocketContextValue | null>(null);

interface UploadSocketProviderProps {
  children: ReactNode;
  path: string;
  successMessage?: string;
}

const UPLOAD_CLIENT_ID_STORAGE_KEY = "upload_client_id";

const getOrCreateClientId = (): string => {
  if (typeof window === "undefined") return Date.now().toString();
  try {
    const existing = window.sessionStorage.getItem(UPLOAD_CLIENT_ID_STORAGE_KEY);
    if (existing) return existing;
    const fresh = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    window.sessionStorage.setItem(UPLOAD_CLIENT_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return Date.now().toString();
  }
};

export const UploadSocketProvider = ({
  children,
  path,
  successMessage,
}: UploadSocketProviderProps) => {
  const [clientId] = useState(() => getOrCreateClientId());
  const listenersRef = useRef<Set<(update: UploadUpdate) => void>>(new Set());

  const addListener = useCallback((listener: (update: UploadUpdate) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const handleUpdate = useCallback((update: UploadUpdate) => {
    listenersRef.current.forEach((listener) => {
      try {
        listener(update);
      } catch (err) {
        console.error("Error in upload socket listener:", err);
      }
    });
  }, []);

  const { connected } = useUploadSocket({
    clientId,
    path,
    onUpdate: handleUpdate,
  });

  useEffect(() => {
    if (connected && successMessage) {
      showToast("success", successMessage);
    }
  }, [connected, successMessage]);

  return (
    <UploadSocketContext.Provider value={{ connected, clientId, addListener }}>
      {children}
    </UploadSocketContext.Provider>
  );
};

export const useUploadSocketContext = () => {
  const context = useContext(UploadSocketContext);
  if (!context) {
    throw new Error(
      "useUploadSocketContext debe usarse dentro de UploadSocketProvider"
    );
  }
  return context;
};

export const useUploadSocketSubscription = (
  onUpdate?: (update: UploadUpdate) => void
) => {
  const { connected, clientId } = useUploadSocketContext();
  const { addListener } = useUploadSocketContext();
  const callbackRef = useRef(onUpdate);

  useEffect(() => {
    callbackRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    const stableListener = (update: UploadUpdate) => {
      callbackRef.current?.(update);
    };
    const removeListener = addListener(stableListener);
    return () => {
      removeListener();
    };
  }, [addListener]);

  return { connected, clientId };
};
